import Highcharts from 'highcharts';
import heatmap from 'highcharts/modules/heatmap';
import exporting from 'highcharts/modules/exporting';
import { Link, useLocation } from 'react-router-dom';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import DataEndpoints from '../../../Services/DataEndpoints';
import { ReportTypes } from '../../../Constants/ReportTypes';
import { NavigationRoutes } from '../../../Constants/Routes';
import { useCreateToast } from '../../../Hooks/useCreateToast';
import { numberToWords } from '../../../Utility/SharedUtility.js';
import exportingOffline from 'highcharts/modules/offline-exporting';
import ReportingEndpoints from '../../../Services/ReportingEndpoints';
import VisualizeByNameFilterCard from './VisualizeByNameFilterCard.js';
import { VisualizeByNameFilterPanel } from './VisualizeByNameFilterPanel.js';
import Outline64Circle from '@mds/mds-icons/icons/svg/outline-64-circle.svg';
// import ProductLevelMarketSize from './ChartComponents/ProductLevelMarketSize';
import MarginTrendsByProducts from './ChartComponents/MarginTrendsByProducts.js';
import Outline24Database from '@mds/mds-icons/icons/svg/outline-24-database.svg';
// import ProductLevelPocketGrowth from './ChartComponents/ProductLevelPocketGrowth.js';
import { buildTree, getAttributesMap } from '../../../Utility/TreeUtility';
import {
  applyAttributeTreeToDimensions,
  buildDefaultSelectionsForDimensions,
} from './visualizeByNameDimensionHelpers';
import {
  buildLiveDashboardDefaultSelections,
  isLiveDashboardChart,
} from './liveDashboardDefaults';
import { applyLiveDashboardGeographyResolutionToSelections } from './liveDashboardGeography';
import { applyLiveDashboardAssetClassDisplayResolutionToSelections, applyLiveDashboardAssetClassResolutionToSelections } from './liveDashboardAssetClass';
import { isLiveDashboardCurrencyDimension, parseCurrencySelectionToCode } from './liveDashboardCurrency';
import { Container, FilterContainer, Sidebar, HelpSection } from './VisualizeByNameContent.styles.js';
import ProductLevelVolumeAndRevenueGrowthTrends from './ChartComponents/ProductLevelVolumeAndRevenueGrowthTrends.js';
// import ProductLevelVolumeAndRevenueDistribution from './ChartComponents/ProductLevelVolumeAndRevenueDistribution.js';
// import ProductLevelImpactOnVolumeANDRevenueGrowth from './ChartComponents/ProductLevelImpactOnVolumeANDRevenueGrowth.js';
import NetFlowsHeatmap from './ChartComponents/NetFlowsHeatmap.js';
import AumHeatmap from './ChartComponents/AumHeatmap.js';
import AumAssetClassAndGeographyHeatmap from './ChartComponents/AumAssetClassAndGeographyHeatmap.js';
import RevenueHeatmap from './ChartComponents/RevenueHeatmap.js';
import RevenueAssetClassAndGeographyHeatmap from './ChartComponents/RevenueAssetClassAndGeographyHeatmap.js';
import LiveDashboard from './ChartComponents/LiveDashboard.js';
import NetFlowsAssetClassAndGeographyHeatmap from './ChartComponents/NetFlowsAssetClassAndGeographyHeatmap.js';
import { Alert, Button, DropdownButton, DropdownItem, getAlertUniqueId, Icon, INFORMATION_ALERT_GLOBAL, PRIMARY_BUTTON, SECONDARY_BUTTON, SUCCESS_ALERT_GLOBAL, TYPE_OUTLINE } from '@mds/mds-reactjs-library';
import { Badge } from '@mds/mds-reactjs-library'
import axios from "axios";
import LocalStorage, { Keys } from '../../../Storage/LocalStorage';
import { Source } from '../../../Constants/Source';

heatmap(Highcharts)
exporting(Highcharts)
exportingOffline(Highcharts)

const ChartLoadingIcon = () => (
  <Icon fill="#2251FF" type={TYPE_OUTLINE} src={Outline64Circle} />
)

const expandSelectionsBetween = (attributeIds, selectedIds) => {
  const ids = [...selectedIds];
  let extendedAttributeIds = [];
  let foundFirstMatch = false;
  for (let attributeId of attributeIds) {
    let position = ids.indexOf(attributeId);
    if (position >= 0)
      foundFirstMatch = true;
    if (foundFirstMatch) {
      if (position >= 0)
        ids.splice(position, 1);
      extendedAttributeIds.push(attributeId);
    }
    if (ids.length <= 0)
      break;
  }
  return extendedAttributeIds;
}

const expandYearsRange = (dimensionAttributes, selectedIds) => {
  const collectLeaves = (nodes) => {
    const leaves = [];
    const walk = (list) => {
      for (const n of list || []) {
        if (n.Children && n.Children.length > 0) walk(n.Children);
        else leaves.push(n);
      }
    };
    walk(nodes);
    return leaves;
  };

  const parseGroup = (displayName) => {
    const str = String(displayName ?? '').trim();
    const colonIdx = str.indexOf(':');
    if (colonIdx < 0) return '';
    return str.substring(0, colonIdx).trim().toLowerCase();
  };

  const parseYear = (displayName) => {
    const m = String(displayName ?? '').match(/\d{4}/);
    return m ? parseInt(m[0], 10) : NaN;
  };

  const allLeaves = collectLeaves(dimensionAttributes);
  const startAttr = allLeaves.find(a => a.AttributeID === selectedIds[0]);
  const endAttr = allLeaves.find(a => a.AttributeID === selectedIds[selectedIds.length - 1]);
  const startGroup = startAttr ? parseGroup(startAttr.DisplayName) : '';
  const endGroup = endAttr ? parseGroup(endAttr.DisplayName) : '';
  const startYear = startAttr ? parseYear(startAttr.DisplayName) : NaN;
  const endYear = endAttr ? parseYear(endAttr.DisplayName) : NaN;

  let extendedAttributeIds = [];
  if (startGroup === endGroup) {
    for (const leaf of allLeaves) {
      const g = parseGroup(leaf.DisplayName);
      const y = parseYear(leaf.DisplayName);
      if (g === startGroup && !Number.isNaN(y) && y >= startYear && y <= endYear) {
        extendedAttributeIds.push(leaf.AttributeID);
      }
    }
  } else {
    for (const leaf of allLeaves) {
      const g = parseGroup(leaf.DisplayName);
      const y = parseYear(leaf.DisplayName);
      if (Number.isNaN(y)) continue;
      if (g === startGroup && y >= startYear) {
        extendedAttributeIds.push(leaf.AttributeID);
      } else if (g === endGroup && y <= endYear) {
        extendedAttributeIds.push(leaf.AttributeID);
      }
    }
  }
  return extendedAttributeIds;
}

const VisualizeByNameContent = ({ chart, chartConfiguration, reUseReport }) => {
  const [dimensions, setDimensions] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [getChartDataCallInProgress, setGetChartDataCallInProgress] = useState(false);
  const [filterPanelIsOpen, setFilterPanelIsOpen] = useState(false);
  const [openAccordionDimensionId, setOpenAccordionDimensionId] = useState(null);

  const [showReportPopup, setShowReportPopup] = useState(false);
  const [reportName, setReportName] = useState('');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showSelectionApplied, setShowSelectionApplied] = useState(false);
  const [datasetId, setDatasetId] = useState(null);
  const [datasetName, setDatasetName] = useState(null);
  const [publicationId, setPublicationId] = useState(null);
  const [selections, setSelections] = useState([]);
  const [selectionsToPost, setSelectionsToPost] = useState([]);
  const [selectedAttributeNamesByDimension, setSelectedAttributeNamesByDimension] = useState({});
  const { createErrorToast } = useCreateToast();
  const chartRef = useRef(null);
  const chart2Ref = useRef(null);
  const liveDashboardInitialLoadDoneRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [headMessage, setHeadMessage] = useState('');
  const [chartTitle, setChartTitle] = useState('');
  const [decimalPointerChartLevel, setDecimalPointerChartLevel] = useState(0)
  const [dimensionText] = useState("Dimensions");
  const [selectAllHide] = useState("SelectAllAndHide");
  const [originalDimensionID] = useState("OriginalDimensionID");
  const [whitelistedAttributeIds] = useState("WhitelistedAttributeIds");
  const [dimensionIDText] = useState("DimensionID");
  const [axis] = useState("Axis");
  const [displayNameText] = useState("DisplayName");
  const subscriptionName = LocalStorage.get(Keys.CURRENT_SUBSCRIPTION).name;
  const dimensionConfigurations = useMemo(() => chartConfiguration?.[dimensionText] || {}, [chartConfiguration, dimensionText]);
  const getDimensionConfiguration = (dimensionName) => dimensionConfigurations?.[dimensionName] || {};
  const dimensionsById = useMemo(() => new Map((dimensions || []).map(dimension => [dimension.DimensionID, dimension])), [dimensions]);
  const selectionsByDimensionId = useMemo(() => new Map((selections || []).map(selection => [selection.DimensionID, selection])), [selections]);
  const configuredDimensions = useMemo(
    () => (dimensions || []).filter(dimension => !getDimensionConfiguration(dimension.Name)[selectAllHide]),
    [dimensions, dimensionConfigurations, selectAllHide]
  );

  const buildChartDefaultSelections = (metadata, options = {}) => {
    if (isLiveDashboardChart(chart)) {
      return buildLiveDashboardDefaultSelections(metadata, getDimensionConfiguration, selectAllHide, options);
    }
    return buildDefaultSelectionsForDimensions(metadata, getDimensionConfiguration, selectAllHide, options);
  };

  const matchedKey = chartConfiguration.DatasetName
  ? Object.keys(chartConfiguration.DatasetName).find(
      key => key.toLowerCase() === subscriptionName.toLowerCase()
    )
  : undefined;

  const MatcheddatasetName = matchedKey ? chartConfiguration.DatasetName[matchedKey] : 'undefined';
  const effectiveDatasetName = datasetName || MatcheddatasetName;
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [chartTitle]);

  useEffect(() => {
    setFilterPanelIsOpen(false);
    setDecimalPointerChartLevel(chartConfiguration.ChartLevelNumberOfDecimalPlaces)
  }, [location.pathname]);

  const filterPanelApplyClicked = () => {
    loadChart();
  }

  useEffect(() => {
    let selectionTimer, successTimer;

    if (showSelectionApplied) {
      selectionTimer = setTimeout(() => {
        setShowSelectionApplied(false);
      }, 3000);
    }

    if (showSuccessNotification) {
      successTimer = setTimeout(() => {
        setShowSuccessNotification(false);
      }, 10000);
    }

    return () => {
      if (selectionTimer) clearTimeout(selectionTimer);
      if (successTimer) clearTimeout(successTimer);
    };
  }, [showSelectionApplied, showSuccessNotification]);

  const exportChart = (format) => {
    if (format === 'text/csv' && isLiveDashboardChart(chart)) {
      chartRef?.current?.downloadCombinedCsv?.();
      return;
    }

    const runExport = (hcChart) => {
      if (!hcChart) return
      hcChart.exportChartLocal({
        filename: hcChart?.title?.textStr,
        type: format,
        fallbackToExportServer: false,
      })
    }
    runExport(chartRef?.current?.chart)
    runExport(chart2Ref?.current?.chart)
  }

  const setSelection = (dimensionId, attributeIds) => {
    const updatedSelections = selections.map((selection) => {
      if (selection.DimensionID === dimensionId) {
        const dimension = dimensionsById.get(dimensionId);
        if (!dimension) {
          return selection;
        }
        const dimensionName = dimension.Name;
        let dimensionConfiguration = getDimensionConfiguration(dimensionName);
        const minSelections = Number.isFinite(dimensionConfiguration.MinSelections) ? dimensionConfiguration.MinSelections : 0;
        const maxSelections = Number.isFinite(dimensionConfiguration.MaxSelections) ? dimensionConfiguration.MaxSelections : Number.MAX_SAFE_INTEGER;
        let isValid = attributeIds.length >= minSelections && attributeIds.length <= maxSelections;
        let isAvoidParentOverlap = !dimensionConfiguration.SingleSelect && dimensionConfiguration.AvoidParentOverlap
        return {
          ...selection,
          Attributes: attributeIds,
          IsValid: isValid,
          IsAvoidParentOverlap: isAvoidParentOverlap
        };
      }
      return selection;
    });
    setSelections(updatedSelections);
  }

  const processDimensions = (metadata) => {
    let newDimensions = [];

    metadata.forEach(dimension => {
      const dimensionConfiguration = getDimensionConfiguration(dimension.Name);
      dimension.Attributes = dimension.Attributes.filter(attribute =>
        !dimensionConfiguration[whitelistedAttributeIds] ||
        dimensionConfiguration[whitelistedAttributeIds].length <= 0 ||
        dimensionConfiguration[whitelistedAttributeIds].includes(attribute.AttributeID)
      );

      if (dimensionConfiguration["Type"] === "Grouped") {
        dimensionConfiguration["Groups"].forEach(group => {
          let newDimension = { ...dimension };
          newDimension[originalDimensionID] = newDimension[dimensionIDText];
          newDimension[dimensionIDText] = dimension[dimensionIDText] + group[axis];
          newDimension["Name"] = group[axis];
          newDimension[displayNameText] = group[displayNameText];
          newDimensions.push(newDimension);
          chartConfiguration[dimensionText][group[axis]] = { ...group };
        });
      }
    });

    metadata = metadata.filter(dimension => !newDimensions.map(newDim => newDim.OriginalDimensionID).includes(dimension.DimensionID));
    metadata.push(...newDimensions);

    metadata.forEach(dimension => {
      const dimensionConfiguration = getDimensionConfiguration(dimension.Name);
      dimension.Attributes = dimension.Attributes.filter(attribute =>
        !dimensionConfiguration[whitelistedAttributeIds] ||
        dimensionConfiguration[whitelistedAttributeIds].length <= 0 ||
        dimensionConfiguration[whitelistedAttributeIds].includes(attribute.AttributeID)
      );
    });

    return metadata;
  }

  const loadChart = () => {
    if (!dimensions || dimensions.length <= 0)
      return;

    setGetChartDataCallInProgress(true)

    try {
    const findAllParents = (attributeId, data) => {
      let hierarchy = [];
      let currentId = attributeId;
      let visited = new Set();
      const attributeMap = getAttributesMap(data.Attributes);

      while (currentId !== null) {

        if (visited.has(currentId)) {
          break;
        }

        const currentAttribute = attributeMap.get(currentId);

        if (currentAttribute) {
          hierarchy.push(currentAttribute.AttributeID);
          visited.add(currentId);
          if (currentAttribute.ParentAttributeID === null) {
            break;
          }
          currentId = currentAttribute.ParentAttributeID;
        } else {
          break;
        }
      }
      return hierarchy.filter(item => item !== attributeId).reverse();
    };

    let updateSelections = isLiveDashboardChart(chart)
      ? applyLiveDashboardGeographyResolutionToSelections(selections, dimensions)
      : selections;
    if (isLiveDashboardChart(chart)) {
      updateSelections = applyLiveDashboardAssetClassResolutionToSelections(updateSelections, dimensions);
    }
    let selectionsWithoutParent = updateSelections;
    let resultArray = [];
    let selectedValue = selectionsWithoutParent.filter(selection => selection.IsAvoidParentOverlap === true);
    selectedValue.forEach(dim => {
      if (dim !== undefined && Array.isArray(dim.Attributes)) {
        for (let selectedAttributeId of dim.Attributes) {
          let values = findAllParents(selectedAttributeId, dimensions.find(dimension => dimension.DimensionID === dim.DimensionID))
          values.forEach(value => {
            if (!resultArray.includes(value)) {
              resultArray.push(value);
            }
          });
        }
      }

      updateSelections = selectionsWithoutParent.map(selection => {
        if (selection.DimensionID === dim.DimensionID) {
          const updatedAttributes = selection.Attributes.filter(attribute =>
            !resultArray.includes(attribute));
          return {
            ...selection,
            Attributes: updatedAttributes,
          };
        }
        return selection;
      });
      selectionsWithoutParent = updateSelections;
    });

    let attributeNamesByDimension = [];
    const displaySelections = isLiveDashboardChart(chart)
      ? applyLiveDashboardAssetClassDisplayResolutionToSelections(updateSelections, dimensions)
      : updateSelections;
    for (let dimension of dimensions) {
      let selectedAttributeIds = displaySelections.find(selection => selection.DimensionID === dimension.DimensionID)?.Attributes;
      if (isLiveDashboardChart(chart) && isLiveDashboardCurrencyDimension(chart, dimension)) {
        attributeNamesByDimension[dimension.Name] = [parseCurrencySelectionToCode(selectedAttributeIds, dimension)];
      } else {
        let attributesMap = getAttributesMap(dimension.Attributes);
        attributeNamesByDimension[dimension.Name] = (selectedAttributeIds ?? [])
          .map((attributeId) => attributesMap.get(attributeId)?.DisplayName)
          .filter(Boolean);
      }
    }
    setSelectedAttributeNamesByDimension(attributeNamesByDimension);

    let selectionsToPost = [];
    for (let selection of updateSelections) {
      let newAttributeIds = [...selection.Attributes];
      const dimension = dimensions.find(dimension => dimension.DimensionID === selection.DimensionID);
      if (getDimensionConfiguration(dimension.Name)["IncludeSelectionsInBetween"] && selection.Attributes.length > 1) {
        const allAttributeIds = dimension.Attributes.map(attribute => attribute.AttributeID);
        newAttributeIds = expandSelectionsBetween(allAttributeIds, newAttributeIds);
      } else if (dimension.DisplayName === 'Years' && selection.Attributes.length > 1) {
        newAttributeIds = expandYearsRange(dimension.Attributes, newAttributeIds);
      }

      if (dimension.hasOwnProperty(originalDimensionID)) {
        let selectionToPost = selectionsToPost.find(selection => selection.DimensionID === dimension[originalDimensionID]);
        if (!selectionToPost) {
          selectionsToPost.push({ DimensionID: dimension[originalDimensionID], Attributes: [], Groups: [], IsValid: true });
          selectionToPost = selectionsToPost.find(selection => selection.DimensionID === dimension[originalDimensionID]);
        }
        selectionToPost["Attributes"].push(...newAttributeIds);
        selectionToPost["Groups"].push({ "Axis": dimension.Name, "Attributes": newAttributeIds });
      }
      else
        selectionsToPost.push({ DimensionID: selection.DimensionID, Attributes: newAttributeIds, IsValid: selections.find(select => select.DimensionID === selection.DimensionID).IsValid });
    }

    setSelectionsToPost(selectionsToPost);
    const payload = {
      Selections: JSON.stringify(selectionsToPost.map(selection => { return { DimensionID: selection.DimensionID, Attributes: selection.Attributes, Groups: selection.Groups } })),
      ChartName: chartTitle,
      DatasetName: effectiveDatasetName
    }
    axios.defaults.headers.common['DatasetName'] = effectiveDatasetName;
    DataEndpoints
      .getChartData(payload)
      .then(({ data }) => {
        if (chartConfiguration.NumberOfDecimalPlaces >= 0 && !isLiveDashboardChart(chart)) {
          let decimalPlaces = chartConfiguration.NumberOfDecimalPlaces;
          data.dataPoints.forEach(point => {
            if (point.value) {
              point.value = parseFloat(point.value.toFixed(decimalPlaces));
            }
            if (point.initialValue) {
              point.initialValue = parseFloat(point.initialValue.toFixed(decimalPlaces));
            }
            if (point.xValue) {
              point.xValue = parseFloat(point.xValue.toFixed(decimalPlaces));
            }
            if (point.yValue) {
              point.yValue = parseFloat(point.yValue.toFixed(decimalPlaces));
            }
          });
        }
        setChartData(data);
        setFilterPanelIsOpen(false);
      })
      .catch(e => createErrorToast(e))
      .finally(() => {
        setGetChartDataCallInProgress(false)
      })
    } catch (error) {
      setGetChartDataCallInProgress(false);
      createErrorToast(error);
    }
  }

  useEffect(() => {
    if (showSelectionApplied) {
      loadChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSelectionApplied]);

  function expandAttributes(attributes, maxlevel = 100, level = 1) {
    return attributes.map(attribute => {

      if (level > maxlevel) {
        attribute.IsExpanded = false;
      }
      else {
        attribute.IsExpanded = true;
      }
      if (attribute.Children) {
        attribute.Children = expandAttributes(attribute.Children, maxlevel, level + 1);
      }

      return attribute;
    });
  }

  useEffect(() => {
    if (reUseReport) {
      setLoading(true);
      setDatasetId(reUseReport.datasetId);
      setPublicationId(reUseReport.generatedData.publicationId);
      DataEndpoints.getMetadata(reUseReport.generatedData.publicationId)
        .then(({ data: metadata }) => {
          metadata = processDimensions(metadata)
          setDimensions(metadata);
          applyAttributeTreeToDimensions(metadata, getDimensionConfiguration, buildTree, expandAttributes)
          let selectionsToReUse = JSON.parse(reUseReport.generatedData.selections);
          let groupSelections = [];
          let dimensionIdsToRemove = [];
          selectionsToReUse.forEach(selection => {
            if (selection.hasOwnProperty("Groups")) {
              dimensionIdsToRemove.push(selection.DimensionID);
              selection["Groups"].forEach(group => groupSelections.push({ DimensionID: selection.DimensionID + group[axis], Attributes: group["Attributes"] }));
            }
          });
          selectionsToReUse.push(...groupSelections);
          selectionsToReUse = selectionsToReUse.filter(selection => !dimensionIdsToRemove.includes(selection.DimensionID));
          setSelections(selectionsToReUse);
          setShowSelectionApplied(true);
        })
        .catch(e => createErrorToast(e))
        .finally(() => {
          setLoading(false);
        });
    }
    else {
      setLoading(true);
      const publicationsRequest = chart?.datasetId
        ? DataEndpoints.getPublications(chart.datasetId, null)
        : DataEndpoints.getPublications(null, MatcheddatasetName);

      publicationsRequest
        .then(({ data: publications }) => {
          let publication = publications.find((publication) => publication?.publicationStatus.name.toLowerCase() === 'live')
          publication = publication || (publications ? publications[0] : null);
          if (publication) {
            setDatasetId(publication.dataset.datasetId);
            setDatasetName(publication.dataset.name);
            setPublicationId(publication.publicationId);
            DataEndpoints
              .getMetadata(publication.publicationId)
              .then(({ data: metadata }) => {
                metadata = processDimensions(metadata)
                setDimensions(metadata);
                applyAttributeTreeToDimensions(metadata, getDimensionConfiguration, buildTree, expandAttributes)
                setSelections(buildChartDefaultSelections(metadata, {
                  includeAvoidOverlap: true,
                  useWhitelistForSingle: !isLiveDashboardChart(chart),
                }));
              })
              .catch(e => createErrorToast(e))
              .finally(() => {
                setLoading(false);
              });
          } else {
            setLoading(false);
          }
        })
        .catch(e => {
          createErrorToast(e);
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  const LoadingSpinner = () => (
    <div className="loadingContainer">
      <ChartLoadingIcon />
    </div>
  );

  useEffect(() => {
    liveDashboardInitialLoadDoneRef.current = false;
    setChartTitle(chart?.title);
    if (dimensions?.length) {
      setSelections(buildChartDefaultSelections(dimensions, {
        includeAvoidOverlap: isLiveDashboardChart(chart),
      }));
    }
    setChartData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  useEffect(() => {
    if (!isLiveDashboardChart(chart) || loading || reUseReport || liveDashboardInitialLoadDoneRef.current) {
      return;
    }
    if (!dimensions?.length || !selections?.length) {
      return;
    }

    const selectionsReady = selections.every(
      (selection) => Array.isArray(selection.Attributes) && selection.Attributes.length > 0
    );
    if (!selectionsReady) {
      return;
    }

    liveDashboardInitialLoadDoneRef.current = true;
    loadChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, dimensions, selections, loading, reUseReport]);

  const extractDataButtonClicked = (e) => {
    if (reportName.trim().length === 0)
      return
    e.target.disabled = true
    e.target.value = 'Generating ...'
    ReportingEndpoints
      .createReportRequestWithSelections({
        datasetId: datasetId,
        chartId: chart.chartId,
        publicationId: publicationId,
        reportStatusId: 1,
        name: reportName,
        reportTypeName: ReportTypes.Default,
        sourceName: Source.CHART,
        selections: JSON.stringify(selectionsToPost),
        placeDataGenerationRequest: true
      })
      .catch(e => createErrorToast(e))
      .finally(() => {
        e.target.disabled = false
        setShowReportPopup(false)
        setReportName('')
        setShowSuccessNotification(true)
      })
  }

  const renderChartComponent = () => {
    switch (chartTitle.toLowerCase()) {
      // case "market size by product and geography":
      //   return <ProductLevelMarketSize chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} setHeadMessage={setHeadMessage} />;
      // case "pocket of growth by product and geography":
      //   return <ProductLevelPocketGrowth chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} scale={chartConfiguration.Scale} setHeadMessage={setHeadMessage} />;
      case "volume and revenue growth trends":
        return <ProductLevelVolumeAndRevenueGrowthTrends chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} scale={chartConfiguration.Scale} />;
      // case "volume and revenue distribution trends":
      //   return <ProductLevelVolumeAndRevenueDistribution chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} scale={chartConfiguration.Scale} />;
      case "margin trends by product":
        return <MarginTrendsByProducts Chart={chart} ChartData={chartData} ChartRef={chartRef} SelectedAttributeNamesByDimension={selectedAttributeNamesByDimension} />;
      // case "impact on volume and revenue growth by product":
      //   return <ProductLevelImpactOnVolumeANDRevenueGrowth chart={chart} chartData={chartData} chartRef={chartRef} chart2Ref={chart2Ref} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} setHeadMessage={setHeadMessage} decimalPointer={decimalPointerChartLevel} />;
      case "net flows by geography and asset class":
        return <NetFlowsAssetClassAndGeographyHeatmap chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} setHeadMessage={setHeadMessage} />;
      case "net flows by investor type and asset class":
        return <NetFlowsHeatmap chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} setHeadMessage={setHeadMessage} />;
      case "aum by client segment and asset class":
        return <AumHeatmap chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} setHeadMessage={setHeadMessage} />;
      case "aum by geography and asset class":
        return <AumAssetClassAndGeographyHeatmap chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} setHeadMessage={setHeadMessage} />;
      case "revenue by client segment and asset class":
        return <RevenueHeatmap chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} setHeadMessage={setHeadMessage} />;
      case "revenue by geography and asset class":
        return <RevenueAssetClassAndGeographyHeatmap chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} setHeadMessage={setHeadMessage} />;
      case "market snapshot":
        return <LiveDashboard chart={chart} chartData={chartData} chartRef={chartRef} selectedAttributeNamesByDimension={selectedAttributeNamesByDimension} setHeadMessage={setHeadMessage} />;
      default:
        return  <div style={{ position: 'relative', width:'100%', height: '100%' }}>
          <img width={'100%'} alt={chartTitle || ""} src={chart?.preview || (chart && `data:${chart?.thumbnailMimetype};base64,${chart?.thumbnail}`)} />
          <Badge type='warning' style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '180px',
            height: '40px',
            fontSize: '16px',
            transform: 'translate(-50%, -50%) rotate(0deg)',
          }}>IN DEVELOPMENT</Badge>
        </div>;
    }
  };

  const fileTypes = [
    { format: 'image/jpeg', label: 'Download JPG' },
    { format: 'image/png', label: 'Download PNG' },
    { format: 'application/pdf', label: 'Download PDF' },
    { format: 'image/svg+xml', label: 'Download SVG' },
    ...(isLiveDashboardChart(chart)
      ? [{ format: 'text/csv', label: 'Download CSV' }]
      : []),
  ];

  return (
    <>
      <Container>
        {
          showSuccessNotification && <Alert
            id={getAlertUniqueId()}
            type={INFORMATION_ALERT_GLOBAL}
            onCloseClick={() => setShowSuccessNotification(false)}
          >
            Successfully added to <Link to={NavigationRoutes.REPORTS}>Library</Link>!
            From there, you can download the raw data in multiple formats.
          </Alert>
        }
        {
          showSelectionApplied &&
          (
            <Alert id={getAlertUniqueId()} type={SUCCESS_ALERT_GLOBAL} style={{ marginBottom: '12px', 'background': '#117e1a' }} onCloseClick={e => setShowSelectionApplied(false)}>
              Selection applied!
            </Alert>
          )
        }
        {
          showReportPopup &&
          <div className='reportPopupWrapper'>
            <div className='reportPopup'>
              <h1>Name your selection</h1>
              <p>This name will appear in your Library.</p>
              <input type="text" placeholder='Name' value={reportName} onChange={e => setReportName(e.target.value)} />
              <div className="buttons">
                <Button appearance={SECONDARY_BUTTON} onClick={() => setShowReportPopup(false)}>Cancel</Button>
                <Button appearance={PRIMARY_BUTTON} onClick={extractDataButtonClicked}>Save selection</Button>
              </div>
            </div>
          </div>
        }
        {loading ? (
          <div className="loadingWrapper">
            <LoadingSpinner />
          </div>
        ) : (
          <FilterContainer>
            <Sidebar>
              {
                dimensions?.length > 0 ? (
                  <div className="header">
                    {/* <h3>Filters</h3> */}
                    <p className="instruction">Make your selections for each of the following categories, then click ‘Apply’. {headMessage}</p>
                    <div className="SectionContainer">
                      <div className="filterButtonContainer">
                        {
                          configuredDimensions
                            .map(dimension =>
                              <VisualizeByNameFilterCard
                                key={dimension.DimensionID}
                                chart={chart}
                                dimension={dimension}
                                selection={selectionsByDimensionId.get(dimension.DimensionID)}
                                setFilterPanelIsOpen={setFilterPanelIsOpen}
                                setOpenAccordionDimensionId={setOpenAccordionDimensionId}
                              />)
                        }

                      </div>
                      
                    </div>
                  </div>) : (
                  <div>
                    <p className="instruction">
                      The interactive chart is currently being developed. In the meantime, we can create a custom chart with the data you need.
                      Please click <a href="mailto:performancelens_growthcube@mckinsey.com">here</a> to submit your request.
                    </p>
                  </div>
                )}
              <br />
              {
                getChartDataCallInProgress ?
                  (
                    <div className="loadingWrapper">
                      <ChartLoadingIcon />
                    </div>
                  ) :
                  (
                    renderChartComponent()
                  )}
            </Sidebar>
          </FilterContainer>)}
        {
          filterPanelIsOpen &&
          (
            <VisualizeByNameFilterPanel
              setFilterPanelIsOpen={setFilterPanelIsOpen}
              openAccordionDimensionId={openAccordionDimensionId}
              setOpenAccordionDimensionId={setOpenAccordionDimensionId}
              dimensions={dimensions}
              selections={selections}
              setSelection={setSelection}
              filterPanelApplyClicked={filterPanelApplyClicked}
              chart={chart}
              chartConfiguration={chartConfiguration}
              headMessage={headMessage}
            />
          )
        }
        {/* <div className="helpSection">
          <HelpSection>
            <span>Don’t see the chart you need?</span>
            <a href="mailto:performancelens_growthcube@mckinsey.com">Get in touch with our team</a>
          </HelpSection>
        </div> */}
      </Container>
    </>
  )
}

export default VisualizeByNameContent
